import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import {
  Upload, ArrowRight, ArrowLeft, Check, Loader2, AlertCircle,
  FileSpreadsheet, Save, Trash2, Download
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = [
  { key: 'upload', icon: Upload },
  { key: 'mapping', icon: FileSpreadsheet },
  { key: 'preview', icon: Check },
  { key: 'options', icon: Save },
  { key: 'import', icon: Download },
];

const FIELD_OPTIONS = [
  { key: 'ignore', label_fr: 'Ignorer', label_en: 'Ignore' },
  { key: 'first_name', label_fr: 'Prenom', label_en: 'First Name' },
  { key: 'last_name', label_fr: 'Nom', label_en: 'Last Name' },
  { key: 'email', label_fr: 'Email', label_en: 'Email' },
  { key: 'phone', label_fr: 'Telephone', label_en: 'Phone' },
  { key: 'city', label_fr: 'Ville', label_en: 'City' },
  { key: 'service', label_fr: 'Service interesse', label_en: 'Service interested' },
  { key: 'source', label_fr: 'Source', label_en: 'Source' },
  { key: 'notes', label_fr: 'Notes', label_en: 'Notes' },
];

export function CsvImportWizard({ open, onClose, onImportComplete }) {
  const { language: lang } = useLanguage();
  const t = (fr, en) => lang === 'fr' ? fr : en;

  const [step, setStep] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState(null); // {upload_id, columns, row_count, sample_rows, suggestions}
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [dupStrategy, setDupStrategy] = useState('ignore');
  const [defaultStatus, setDefaultStatus] = useState('new');
  const [defaultSource, setDefaultSource] = useState('other');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [progress, setProgress] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const stepLabels = [
    t('Upload', 'Upload'),
    t('Mapping', 'Mapping'),
    t('Apercu', 'Preview'),
    t('Options', 'Options'),
    t('Import', 'Import'),
  ];

  useEffect(() => {
    if (open) {
      setStep(0);
      setUploadData(null);
      setMapping({});
      setPreview([]);
      setImportResult(null);
      setProgress(0);
      loadTemplates();
    }
  }, [open]);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get('/leads/import/templates');
      setTemplates(data.templates);
    } catch { /* ignore */ }
  };

  // Step 1: Upload
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/leads/import/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadData(data);
      // Apply auto-suggestions
      const initialMapping = {};
      data.columns.forEach(col => {
        initialMapping[col] = data.suggestions?.[col] || 'ignore';
      });
      setMapping(initialMapping);
      setStep(1);
      toast.success(t(
        `${data.row_count} lignes detectees, ${data.columns.length} colonnes`,
        `${data.row_count} rows detected, ${data.columns.length} columns`
      ));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Erreur d\'upload', 'Upload error'));
    } finally { setUploading(false); }
  };

  // Step 2: Apply template
  const applyTemplate = (tpl) => {
    const newMapping = {};
    uploadData.columns.forEach(col => {
      newMapping[col] = tpl.mapping[col] || 'ignore';
    });
    setMapping(newMapping);
    toast.success(t('Template applique', 'Template applied'));
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) return;
    setSavingTemplate(true);
    try {
      const { data } = await api.post('/leads/import/templates', { name: templateName, mapping });
      setTemplates(prev => [data, ...prev]);
      setTemplateName('');
      toast.success(t('Template sauvegarde', 'Template saved'));
    } catch { toast.error(t('Erreur', 'Error')); }
    finally { setSavingTemplate(false); }
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/leads/import/templates/${id}`);
      setTemplates(prev => prev.filter(t => t.template_id !== id));
    } catch { toast.error(t('Erreur', 'Error')); }
  };

  // Step 3: Preview
  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.post('/leads/import/preview', {
        upload_id: uploadData.upload_id,
        mapping
      });
      setPreview(data.preview);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Erreur', 'Error'));
    } finally { setLoadingPreview(false); }
  };

  // Step 5: Execute
  const executeImport = async () => {
    setImporting(true);
    setProgress(0);
    const interval = setInterval(() => setProgress(p => Math.min(p + 3, 90)), 200);
    try {
      const { data } = await api.post('/leads/import/execute', {
        upload_id: uploadData.upload_id,
        mapping,
        duplicate_strategy: dupStrategy,
        default_status: defaultStatus,
        default_source: defaultSource,
      });
      setProgress(100);
      setImportResult(data);
      setStep(4);
      toast.success(t(`${data.imported} prospects importes`, `${data.imported} leads imported`));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Erreur d\'import', 'Import error'));
    } finally {
      clearInterval(interval);
      setImporting(false);
    }
  };

  const downloadErrors = () => {
    if (!importResult?.error_details?.length) return;
    const csv = "Row,Reason\n" + importResult.error_details.map(e => `${e.row},"${e.reason}"`).join("\n");
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'import_errors.csv'; a.click();
  };

  const mappedCount = Object.values(mapping).filter(v => v && v !== 'ignore').length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            {t('Importer des prospects (CSV)', 'Import leads (CSV)')}
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-1 mb-4">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-colors ${
                i <= step ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-xs ml-1.5 hidden sm:inline ${i <= step ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                {stepLabels[i]}
              </span>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i < step ? 'bg-primary' : 'bg-muted'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Upload */}
        {step === 0 && (
          <div className="space-y-4" data-testid="csv-step-upload">
            <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
              <Upload className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
              <p className="font-medium mb-1">{t('Selectionnez un fichier CSV', 'Select a CSV file')}</p>
              <p className="text-sm text-muted-foreground mb-4">{t('Formats: CSV, separateur virgule ou point-virgule', 'Formats: CSV, comma or semicolon separator')}</p>
              <Button asChild disabled={uploading}>
                <label className="cursor-pointer">
                  {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                  {uploading ? t('Analyse...', 'Analyzing...') : t('Choisir le fichier', 'Choose file')}
                  <input type="file" accept=".csv,.txt" className="hidden" onChange={handleFileSelect} data-testid="csv-file-input" />
                </label>
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Mapping */}
        {step === 1 && uploadData && (
          <div className="space-y-4" data-testid="csv-step-mapping">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{uploadData.columns.length} {t('colonnes detectees', 'columns detected')} - {mappedCount} {t('mappees', 'mapped')}</span>
            </div>

            {/* Templates */}
            {templates.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <span className="text-xs text-muted-foreground self-center">{t('Templates:', 'Templates:')}</span>
                {templates.map(tpl => (
                  <div key={tpl.template_id} className="flex items-center gap-1">
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => applyTemplate(tpl)}>
                      {tpl.name}
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => deleteTemplate(tpl.template_id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Mapping form */}
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {uploadData.columns.map(col => (
                <div key={col} className="flex items-center gap-3">
                  <div className="w-1/2 text-sm font-mono bg-muted px-3 py-2 rounded truncate" title={col}>{col}</div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Select value={mapping[col] || 'ignore'} onValueChange={v => setMapping(prev => ({ ...prev, [col]: v }))}>
                    <SelectTrigger className="w-1/2" data-testid={`mapping-${col}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FIELD_OPTIONS.map(f => (
                        <SelectItem key={f.key} value={f.key}>
                          {lang === 'fr' ? f.label_fr : f.label_en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            {/* Save template */}
            <div className="flex gap-2">
              <Input placeholder={t('Nom du template...', 'Template name...')} value={templateName} onChange={e => setTemplateName(e.target.value)} className="flex-1" data-testid="template-name-input" />
              <Button variant="outline" size="sm" onClick={saveTemplate} disabled={!templateName.trim() || savingTemplate}>
                {savingTemplate ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Save className="mr-2 h-3 w-3" />}
                {t('Sauvegarder', 'Save')}
              </Button>
            </div>

            {/* Sample data */}
            {uploadData.sample_rows?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">{t('Voir les premieres lignes brutes', 'View raw first rows')}</summary>
                <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto max-h-[120px]">{JSON.stringify(uploadData.sample_rows[0], null, 2)}</pre>
              </details>
            )}
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 2 && (
          <div className="space-y-4" data-testid="csv-step-preview">
            <p className="text-sm text-muted-foreground">{t('Apercu des 5 premieres lignes avec le mapping applique:', 'Preview of first 5 rows with mapping applied:')}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    {['name','email','phone','city','service','source','notes'].map(f => (
                      <th key={f} className="text-left p-2 font-medium text-xs">{f}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {preview.map((row, i) => (
                    <tr key={`preview-${row.email || row.phone || row.name || ''}-${i}`} className="hover:bg-muted/30">
                      {['name','email','phone','city','service','source','notes'].map(f => (
                        <td key={f} className="p-2 text-xs truncate max-w-[150px]">{row[f] || '-'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.length === 0 && (
              <div className="text-center py-4 text-muted-foreground">
                <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>{t('Aucune donnee dans l\'apercu. Verifiez votre mapping.', 'No data in preview. Check your mapping.')}</p>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Options */}
        {step === 3 && (
          <div className="space-y-5" data-testid="csv-step-options">
            <div className="space-y-2">
              <Label>{t('Gestion des doublons (meme email)', 'Duplicate handling (same email)')}</Label>
              <Select value={dupStrategy} onValueChange={setDupStrategy}>
                <SelectTrigger data-testid="duplicate-strategy"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ignore">{t('Ignorer les doublons', 'Ignore duplicates')}</SelectItem>
                  <SelectItem value="overwrite">{t('Ecraser les existants', 'Overwrite existing')}</SelectItem>
                  <SelectItem value="create">{t('Creer quand meme', 'Create anyway')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('Statut par defaut', 'Default status')}</Label>
              <Select value={defaultStatus} onValueChange={setDefaultStatus}>
                <SelectTrigger data-testid="default-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">{t('Nouveau', 'New')}</SelectItem>
                  <SelectItem value="contacted">{t('Contacte', 'Contacted')}</SelectItem>
                  <SelectItem value="appointment">{t('Rendez-vous', 'Appointment')}</SelectItem>
                  <SelectItem value="quote_sent">{t('Soumission envoyee', 'Quote sent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('Source par defaut', 'Default source')}</Label>
              <Input value={defaultSource} onChange={e => setDefaultSource(e.target.value)} placeholder="other" data-testid="default-source" />
              <p className="text-xs text-muted-foreground">{t('Valeurs: maps, ads, seo, direct, referral, other', 'Values: maps, ads, seo, direct, referral, other')}</p>
            </div>
            <Card className="bg-muted/50">
              <CardContent className="py-3 text-sm">
                <p><strong>{uploadData?.row_count}</strong> {t('lignes a importer', 'rows to import')}</p>
                <p><strong>{mappedCount}</strong> {t('colonnes mappees', 'columns mapped')}</p>
                <p>{t('Doublons', 'Duplicates')}: <strong>{dupStrategy === 'ignore' ? t('Ignores', 'Ignored') : dupStrategy === 'overwrite' ? t('Ecrases', 'Overwritten') : t('Crees quand meme', 'Created anyway')}</strong></p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 5: Import result */}
        {step === 4 && (
          <div className="space-y-4" data-testid="csv-step-result">
            {importing ? (
              <div className="py-8 space-y-3">
                <Loader2 className="h-10 w-10 animate-spin mx-auto text-primary" />
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
                <p className="text-center text-sm text-muted-foreground">{t('Import en cours...', 'Importing...')} {Math.round(progress)}%</p>
              </div>
            ) : importResult && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <Card className="bg-green-500/10 border-green-500/20">
                    <CardContent className="py-4 text-center">
                      <div className="text-2xl font-bold text-green-600">{importResult.imported}</div>
                      <div className="text-xs text-muted-foreground">{t('Importes', 'Imported')}</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-500/10 border-amber-500/20">
                    <CardContent className="py-4 text-center">
                      <div className="text-2xl font-bold text-amber-600">{importResult.duplicates}</div>
                      <div className="text-xs text-muted-foreground">{t('Doublons', 'Duplicates')}</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-500/10 border-red-500/20">
                    <CardContent className="py-4 text-center">
                      <div className="text-2xl font-bold text-red-600">{importResult.errors}</div>
                      <div className="text-xs text-muted-foreground">{t('Erreurs', 'Errors')}</div>
                    </CardContent>
                  </Card>
                </div>
                {importResult.errors > 0 && (
                  <Button variant="outline" size="sm" onClick={downloadErrors}>
                    <Download className="mr-2 h-4 w-4" />{t('Telecharger le rapport d\'erreurs', 'Download error report')}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer navigation */}
        <DialogFooter className="flex justify-between gap-2 sm:justify-between">
          <div>
            {step > 0 && step < 4 && (
              <Button variant="outline" onClick={() => setStep(s => s - 1)}>
                <ArrowLeft className="mr-2 h-4 w-4" />{t('Retour', 'Back')}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step === 1 && (
              <Button onClick={loadPreview} disabled={mappedCount === 0 || loadingPreview} data-testid="preview-btn">
                {loadingPreview ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t('Apercu', 'Preview')} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
            {step === 2 && (
              <Button onClick={() => setStep(3)} data-testid="options-btn">
                {t('Options', 'Options')} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
            {step === 3 && (
              <Button onClick={() => { setStep(4); executeImport(); }} data-testid="execute-import-btn">
                <Download className="mr-2 h-4 w-4" />{t('Lancer l\'import', 'Start import')}
              </Button>
            )}
            {step === 4 && importResult && (
              <Button onClick={() => { onImportComplete?.(); onClose(); }}>
                <Check className="mr-2 h-4 w-4" />{t('Terminer', 'Done')}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
